import React, { useState, useEffect, memo } from 'react';
import { useIntl, FormattedMessage } from 'react-intl';
import { ChevronUp, ChevronDown, Copy, Check } from 'lucide-react';
import { MapPinIcon } from '@heroicons/react/24/outline';
import { trackCompanyLink } from '../../../../../utils/analytics/analytics';
import { CompanyLogo } from './CompanyLogo';
import { CompanyName } from '../../../../../components/CompanyName';
import { SocialMediaButtons } from './SocialMediaButtons';
import { MoneyGraph, MonthlyHiringChart, StarburstChart } from './CompanyCharts';
import { RoleRoleLevelChart } from './RoleRoleLevelChart';
import { DonutSunburstChart } from './DonutSunburstChart';
import { CompanyLocationMap } from './CompanyLocationMap';
import { fixMojibake } from '../../../../../utils/text/fix-mojibake';
import { getFirstCompanyUrl, extractYouTubeVideoId } from '../utils/companyUtils';
import { isValidNumber, hasAnyValidNumber, getValidNumberOrNull } from '../utils/numberUtils';
import type { DisplayJob } from '../../../../../types';

export type CompanyInfoTabId = 'financial' | 'hiring' | 'roles' | 'locations' | 'languages' | 'map';

export interface CompanySectionProps {
  displayJob: DisplayJob;
  companyId?: string | null;
  companyIndustry?: string | null;
  companyAbout?: string | null;
  companyContactDetails?: string | null;
  companyRevenue?: number | null;
  companyProfit?: number | null;
  companyProfitMargin?: number | null;
  companyMonthlyCounts?: Record<string, number> | null;
  companyHeadCountMin?: number | null;
  companyHeadCountMax?: number | null;
  companyHeadcountRangeMin?: number | null;
  companyHeadcountRangeMax?: number | null;
  companyHQInFinland?: string | null;
  companyVideo?: string | null;
  linkedinUrl?: string | null;
  facebookUrl?: string | null;
  instagramUrl?: string | null;
  youtubeUrl?: string | null;
  tiktokUrl?: string | null;
  registryMainBusinessLineDescriptionEn?: string | null;
  registryMainBusinessLineDescriptionFi?: string | null;
  registryMainBusinessLineDescriptionSv?: string | null;
  registryStreet?: string | null;
  registryCity?: string | null;
  registryPostCode?: string | null;
  registryBuildingNumber?: string | null;
  address?: string | null;
  address_fi?: string | null;
  address_sv?: string | null;
  businessID?: string | null;
  registryBusinessId?: string | null;
  companyGeocoordinates?: [number, number] | null;
  currentLocaleTrends?: any;
  jobLocale: string;
  brandColour?: string;
  useBrandColor?: boolean;
  hasAnyCharts: boolean;
  chartsCollapsed: boolean;
  onToggleChartsCollapse: () => void;
  companyRef: React.RefObject<HTMLDivElement>;
}

const DEFAULT_BRAND_GREEN = '#22c55e';

export const CompanySection: React.FC<CompanySectionProps> = memo(({
  displayJob,
  companyId,
  companyIndustry,
  companyAbout,
  companyContactDetails,
  companyRevenue,
  companyProfit,
  companyProfitMargin,
  companyMonthlyCounts,
  companyHeadCountMin,
  companyHeadCountMax,
  companyHeadcountRangeMin,
  companyHeadcountRangeMax,
  companyHQInFinland,
  companyVideo,
  linkedinUrl,
  facebookUrl,
  instagramUrl,
  youtubeUrl,
  tiktokUrl,
  registryMainBusinessLineDescriptionEn,
  registryMainBusinessLineDescriptionFi,
  registryMainBusinessLineDescriptionSv,
  registryStreet,
  registryCity,
  registryPostCode,
  registryBuildingNumber,
  address,
  address_fi,
  address_sv,
  businessID,
  registryBusinessId,
  companyGeocoordinates,
  currentLocaleTrends,
  jobLocale,
  brandColour,
  useBrandColor = false,
  hasAnyCharts,
  chartsCollapsed,
  onToggleChartsCollapse,
  companyRef,
}) => {
  const intl = useIntl();
  const [companyInfoActiveTab, setCompanyInfoActiveTab] = useState<CompanyInfoTabId>('financial');
  const [showCompanyContactDetails, setShowCompanyContactDetails] = useState(false);
  const [showCompanyLocationMap, setShowCompanyLocationMap] = useState(false);
  const [companyNameCopied, setCompanyNameCopied] = useState(false);

  const effectiveBrandColour = (useBrandColor && brandColour) ? brandColour : DEFAULT_BRAND_GREEN;

  // Get first company URL from semicolon-separated list
  const firstCompanyUrl = getFirstCompanyUrl(displayJob?.companyURL);
  const companyUrlToUse = firstCompanyUrl || displayJob?.companyURL;

  // Get main business line description based on locale
  const mainBusinessLine = jobLocale === 'fi' 
    ? registryMainBusinessLineDescriptionFi 
    : jobLocale === 'sv' 
    ? registryMainBusinessLineDescriptionSv 
    : registryMainBusinessLineDescriptionEn;

  // Only show mainBusinessLine if it's different from companyIndustry
  const shouldShowMainBusinessLine = mainBusinessLine && 
    companyIndustry && 
    mainBusinessLine.trim().toLowerCase() !== companyIndustry.trim().toLowerCase();

  // Build address string
  let addressDisplay: string | null = null;
  if (address) {
    addressDisplay = address;
  } else {
    const languageAddress = (displayJob as any)[`address_${jobLocale}`];
    if (languageAddress) {
      addressDisplay = languageAddress;
    } else if (jobLocale === 'en' && address_fi) {
      addressDisplay = address_fi;
    } else if (address_fi) {
      addressDisplay = address_fi;
    } else if (address_sv) {
      addressDisplay = address_sv;
    } else {
      const addressParts = [
        registryStreet,
        registryBuildingNumber,
        registryPostCode,
        registryCity,
      ].filter(Boolean);
      addressDisplay = addressParts.length > 0 ? addressParts.join(' ') : null;
    }
  }

  // Extract YouTube video ID
  const youtubeVideoId = extractYouTubeVideoId(companyVideo);

  // Get business ID
  const businessId = registryBusinessId || businessID;

  // Set default tab based on available data
  useEffect(() => {
    if (!chartsCollapsed) {
      const hasFinancial = hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin);
      const hasHiring = !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0);
      const hasLocations = !!(currentLocaleTrends &&
        ((currentLocaleTrends.locationDistribution &&
          Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
          Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
         (currentLocaleTrends.remotenessDistribution &&
          (currentLocaleTrends.remotenessDistribution.remote > 0 ||
           currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
           currentLocaleTrends.remotenessDistribution.onsite > 0))));
      const hasRoles = !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution));
      
      const currentTabHasData = 
        (companyInfoActiveTab === 'financial' && hasFinancial) ||
        (companyInfoActiveTab === 'hiring' && hasHiring) ||
        (companyInfoActiveTab === 'roles' && hasRoles) ||
        (companyInfoActiveTab === 'locations' && hasLocations) ||
        (companyInfoActiveTab === 'languages' && !!(currentLocaleTrends?.languageRequirements &&
          Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
          Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)))) ||
        (companyInfoActiveTab === 'map' && !!companyGeocoordinates);
      
      if (!currentTabHasData) {
        if (hasFinancial) setCompanyInfoActiveTab('financial');
        else if (hasHiring) setCompanyInfoActiveTab('hiring');
        else if (hasRoles) setCompanyInfoActiveTab('roles');
        else if (hasLocations) setCompanyInfoActiveTab('locations');
        else if (currentLocaleTrends?.languageRequirements &&
          Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
          Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val))) {
          setCompanyInfoActiveTab('languages');
        } else if (companyGeocoordinates) {
          setCompanyInfoActiveTab('map');
        }
      }
    }
  }, [chartsCollapsed, companyRevenue, companyProfit, companyProfitMargin, companyMonthlyCounts, currentLocaleTrends, companyGeocoordinates, companyInfoActiveTab]);

  type TabConfig = {
    id: CompanyInfoTabId;
    label: string;
    labelKey: string;
    hasData: boolean;
  };

  const availableTabs: TabConfig[] = [
    {
      id: 'financial',
      label: 'Financial',
      labelKey: 'jobDetailPage.financial',
      hasData: hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin),
    },
    {
      id: 'hiring',
      label: 'Hiring Trends',
      labelKey: 'jobDetailPage.hiringTrends',
      hasData: !!(companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0),
    },
    {
      id: 'roles',
      label: 'Roles',
      labelKey: 'jobDetailPage.roleAndRoleLevelDistribution',
      hasData: !!(currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution)),
    },
    {
      id: 'locations',
      label: 'Locations',
      labelKey: 'jobDetailPage.locationDistribution',
      hasData: !!(currentLocaleTrends &&
        ((currentLocaleTrends.locationDistribution &&
          Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
          Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
         (currentLocaleTrends.remotenessDistribution &&
          (currentLocaleTrends.remotenessDistribution.remote > 0 ||
           currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
           currentLocaleTrends.remotenessDistribution.onsite > 0)))),
    },
    {
      id: 'languages',
      label: 'Languages',
      labelKey: 'jobDetailPage.languageRequirements',
      hasData: !!(currentLocaleTrends?.languageRequirements &&
        Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
        Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val))),
    },
    {
      id: 'map',
      label: 'Map',
      labelKey: 'jobDetailPage.map',
      hasData: !!companyGeocoordinates,
    },
  ].filter(tab => tab.hasData);

  const topRowTabs = availableTabs.filter(tab => ['financial', 'hiring', 'map'].includes(tab.id));
  const bottomRowTabs = availableTabs.filter(tab => !['financial', 'hiring', 'map'].includes(tab.id));

  const renderTabButton = (tab: TabConfig) => {
    const handleClick = () => {
      setCompanyInfoActiveTab(tab.id);
      if (tab.id === 'map') {
        setShowCompanyLocationMap(true);
      }
    };
    const isActive = companyInfoActiveTab === tab.id;
    const useBrandColorForButton = useBrandColor && brandColour;
    
    return (
      <button
        key={tab.id}
        type="button"
        onClick={handleClick}
        className={`px-5 py-2.5 text-sm font-semibold transition-colors transition-transform duration-200 whitespace-nowrap cursor-pointer rounded-lg border-2 ${
          isActive
            ? useBrandColorForButton
              ? 'text-white shadow-md scale-105'
              : 'bg-blue-600 dark:bg-blue-500 text-white border-blue-600 dark:border-blue-500 shadow-md scale-105'
            : 'bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700 hover:border-gray-300 dark:hover:border-gray-600 hover:shadow-sm active:scale-95'
        }`}
        style={isActive && useBrandColorForButton ? {
          backgroundColor: effectiveBrandColour,
          borderColor: effectiveBrandColour,
        } : undefined}
      >
        <FormattedMessage id={tab.labelKey} defaultMessage={tab.label} />
      </button>
    );
  };

  return (
    <div ref={companyRef} data-section="company" className="scroll-mt-20 mt-10 pt-10 border-t border-gray-200/60 dark:border-gray-700/60" id="company">
      <h2 id="company-header" className="text-2xl font-bold text-gray-900 dark:text-white mb-6 tracking-tight">
        <FormattedMessage 
          id="jobDetailPage.section.company" 
          defaultMessage="Company Information" 
          description="Tab label for company information section showing company details and statistics" 
        />
      </h2>

      <div className="bg-white/90 dark:bg-gray-800/90 border border-gray-200/60 dark:border-gray-700/60 p-4 sm:p-6 rounded-xl transition-colors duration-200 shadow-sm">
        {/* Top Section: Logo, Company Info, Social Media - Responsive Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-[auto_1fr] gap-4 sm:gap-6 mb-6">
          {/* Logo Section */}
          <div className="flex justify-center sm:justify-start">
            {companyUrlToUse ? (
              <a
                href={companyUrlToUse}
                target="_blank"
                rel="noopener noreferrer"
                onClick={() => {
                  trackCompanyLink({
                    companyId: companyId || '',
                    locale: intl.locale,
                  });
                }}
                className="hover:opacity-80 transition-opacity"
              >
                <CompanyLogo
                  companyId={companyId || ''}
                  companyURL={companyUrlToUse}
                  disableLink
                  size="xlarge"
                  isTyomarkkinatori={displayJob?.isTyomarkkinatori}
                />
              </a>
            ) : (
              <CompanyLogo 
                companyId={companyId || ''}
                size="xlarge"
                isTyomarkkinatori={displayJob?.isTyomarkkinatori}
              />
            )}
          </div>
        
          {/* Company Info and Social Media */}
          <div className="flex flex-col gap-4">
            {/* Company Name and Details */}
            <div>
              <div className="flex items-center gap-2 mb-2">
                {companyUrlToUse ? (
                  <a
                    href={companyUrlToUse}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={() => {
                      trackCompanyLink({
                        companyId: companyId || '',
                        locale: intl.locale,
                      });
                    }}
                    className="flex-1 hover:opacity-80 transition-opacity"
                  >
                    <h4 className="font-bold text-xl sm:text-2xl text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 transition-colors">
                      <CompanyName name={companyId ?? ''} />
                    </h4>
                  </a>
                ) : (
                  <h4 className="font-bold text-xl sm:text-2xl text-gray-900 dark:text-white flex-1">
                    <CompanyName name={companyId ?? ''} />
                  </h4>
                )}
                {companyId && (
                  <button
                    onClick={async (e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      try {
                        await navigator.clipboard.writeText(companyId);
                        setCompanyNameCopied(true);
                        setTimeout(() => setCompanyNameCopied(false), 2000);
                      } catch (err) {
                        console.error('Failed to copy company name:', err);
                      }
                    }}
                    className="p-1.5 text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200 hover:bg-gray-100 dark:hover:bg-gray-700 rounded transition-colors"
                    title={intl.formatMessage(
                      { id: 'job.copyCompanyName', defaultMessage: 'Copy company name', description: 'Button tooltip to copy company name to clipboard' },
                      { companyName: companyId }
                    )}
                  >
                    {companyNameCopied ? (
                      <Check className="w-4 h-4 text-green-600 dark:text-green-400" />
                    ) : (
                      <Copy className="w-4 h-4" />
                    )}
                  </button>
                )}
              </div>
              
              {/* Company Details Grid */}
              <div className="space-y-1.5 text-sm">
                {companyIndustry && (
                  <div className="text-gray-600 dark:text-gray-400 font-medium">
                    {fixMojibake(companyIndustry)}
                  </div>
                )}
                {shouldShowMainBusinessLine && (
                  <div className="text-sm text-gray-500 dark:text-gray-400">
                    {fixMojibake(mainBusinessLine)}
                  </div>
                )}
                {addressDisplay && (
                  <div className="text-sm text-gray-500 dark:text-gray-400 flex items-center gap-1.5">
                    <MapPinIcon className="w-4 h-4 flex-shrink-0" />
                    <span>{fixMojibake(addressDisplay)}</span>
                  </div>
                )}
                {businessId && (
                  <div className="text-xs text-gray-400 dark:text-gray-500 font-mono">
                    <FormattedMessage 
                      id="jobDetailPage.businessId" 
                      defaultMessage="Business ID" 
                      description="Label for company business registration ID" 
                    />: {businessId}
                  </div>
                )}
                {(companyHQInFinland || 
                  isValidNumber(companyHeadCountMin) || 
                  isValidNumber(companyHeadCountMax) ||
                  isValidNumber(companyHeadcountRangeMin) || 
                  isValidNumber(companyHeadcountRangeMax)) && (
                  <div className="flex flex-wrap gap-x-4 gap-y-1 pt-1">
                    {companyHQInFinland && (
                      <div className="text-sm text-gray-600 dark:text-gray-400">
                        <strong className="text-gray-900 dark:text-white">HQ:</strong> {fixMojibake(companyHQInFinland)}
                      </div>
                    )}
                    {(() => {
                      const min = getValidNumberOrNull(companyHeadCountMin ?? companyHeadcountRangeMin);
                      const max = getValidNumberOrNull(companyHeadCountMax ?? companyHeadcountRangeMax);
                      const hasValidHeadcount = (min !== null && min > 0) || (max !== null && max > 0);
                      if (!hasValidHeadcount) return null;
                      
                      return (
                        <div className="text-sm text-gray-600 dark:text-gray-400">
                          <strong className="text-gray-900 dark:text-white">
                            <FormattedMessage 
                              id="jobDetailPage.headcount" 
                              defaultMessage="Headcount" 
                              description="Label for company employee count/headcount field" 
                            />:
                          </strong>{' '}
                          {min !== null && max !== null && min === max
                            ? min.toLocaleString()
                            : `${min !== null ? min.toLocaleString() : '?'} - ${max !== null ? max.toLocaleString() : '?'}`}
                        </div>
                      );
                    })()}
                  </div>
                )}
              </div>
            </div>
          
            {/* Social Media and YouTube */}
            <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 flex-wrap">
              <SocialMediaButtons
                linkedinUrl={linkedinUrl}
                facebookUrl={facebookUrl}
                instagramUrl={instagramUrl}
                youtubeUrl={youtubeUrl}
                tiktokUrl={tiktokUrl}
              />
              {youtubeVideoId && (
                <a
                  href={`https://www.youtube.com/watch?v=${youtubeVideoId}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-4 py-2 bg-red-600 hover:bg-red-700 text-white rounded-lg transition-colors text-sm font-medium"
                  onClick={() => {
                    trackCompanyLink({
                      companyId: companyId || '',
                      locale: intl.locale,
                    });
                  }}
                >
                  <svg 
                    className="w-5 h-5" 
                    fill="currentColor" 
                    viewBox="0 0 24 24"
                    aria-hidden="true"
                  >
                    <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                  </svg>
                  <FormattedMessage 
                    id="jobDetailPage.watchCompanyVideo" 
                    defaultMessage="Watch Company Video" 
                    description="Button label to watch company introduction or promotional video" 
                  />
                </a>
              )}
            </div>
          </div>
        </div>

        {/* About Text Section */}
        {companyAbout && (
          <div className="mb-6 pt-6 border-t border-gray-200/60 dark:border-gray-700/60">
            <h5 className="text-sm font-semibold text-gray-900 dark:text-white mb-2">
              <FormattedMessage 
                id="jobDetailPage.aboutCompany" 
                defaultMessage="About" 
                description="Section heading for company description/about text" 
              />
            </h5>
            <p className="text-sm text-gray-700 dark:text-gray-300 leading-relaxed">
              {fixMojibake(companyAbout)}
            </p>
          </div>
        )}

        {/* Contact Details */}
        {companyContactDetails && (
          <div className="mb-6 pt-6 border-t border-gray-200/60 dark:border-gray-700/60">
            {!showCompanyContactDetails ? (
              <button
                type="button"
                className="text-sm text-blue-600 dark:text-blue-400 underline hover:text-blue-800 dark:hover:text-blue-300 px-2 py-1 border border-blue-100 dark:border-blue-700 rounded cursor-pointer"
                onClick={() => setShowCompanyContactDetails(true)}
              >
                <FormattedMessage 
                  id="jobDetailPage.showCompanyContact" 
                  defaultMessage="Show Company Contact" 
                  description="Button label to reveal company contact information" 
                />
              </button>
            ) : (
              <div className="text-sm text-gray-600 dark:text-gray-400">
                <strong className="text-gray-900 dark:text-white">Contact:</strong> {companyContactDetails}
              </div>
            )}
          </div>
        )}

        {/* Charts and Data Grid */}
        <div className="mt-6">
          {/* Collapse/Expand Button */}
          {hasAnyCharts && (
            <button
              type="button"
              onClick={onToggleChartsCollapse}
              className="flex items-center gap-2 mb-4 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white transition-colors cursor-pointer"
            >
              {chartsCollapsed ? (
                <>
                  <ChevronDown className="w-4 h-4" />
                  <FormattedMessage 
                    id="jobDetailPage.showCharts" 
                    defaultMessage="Show Charts" 
                    description="Button label to expand and show company statistics charts" 
                  />
                </>
              ) : (
                <>
                  <ChevronUp className="w-4 h-4" />
                  <FormattedMessage 
                    id="jobDetailPage.hideCharts" 
                    defaultMessage="Hide Charts" 
                    description="Button label to collapse and hide company statistics charts" 
                  />
                </>
              )}
            </button>
          )}

          {!chartsCollapsed && (
            <>
              {/* Top row tabs */}
              {topRowTabs.length > 0 && (
                <div className="mb-4">
                  <div className="flex gap-3 justify-center flex-wrap">
                    {topRowTabs.map(renderTabButton)}
                  </div>
                </div>
              )}

              {/* Chart content */}
              <div className="min-h-[400px] w-full">
                {companyInfoActiveTab === 'financial' && hasAnyValidNumber(companyRevenue, companyProfit, companyProfitMargin) && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl w-full">
                    <MoneyGraph
                      revenue={companyRevenue}
                      profit={companyProfit}
                      profitMargin={companyProfitMargin}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'hiring' && companyMonthlyCounts && Object.keys(companyMonthlyCounts).length > 0 && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl w-full">
                    <MonthlyHiringChart 
                      monthlyCounts={companyMonthlyCounts}
                      headcountMin={getValidNumberOrNull(companyHeadCountMin ?? companyHeadcountRangeMin) ?? undefined}
                      headcountMax={getValidNumberOrNull(companyHeadCountMax ?? companyHeadcountRangeMax) ?? undefined}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'roles' && currentLocaleTrends && (currentLocaleTrends.roleDistribution || currentLocaleTrends.roleLevelDistribution) && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl w-full">
                    <RoleRoleLevelChart
                      roleDistribution={currentLocaleTrends.roleDistribution}
                      roleLevelDistribution={currentLocaleTrends.roleLevelDistribution}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'locations' && currentLocaleTrends &&
                  ((currentLocaleTrends.locationDistribution &&
                    Object.keys(currentLocaleTrends.locationDistribution).length > 0 &&
                    Object.values(currentLocaleTrends.locationDistribution).some(val => hasAnyValidNumber(val))) ||
                   (currentLocaleTrends.remotenessDistribution &&
                    (currentLocaleTrends.remotenessDistribution.remote > 0 ||
                     currentLocaleTrends.remotenessDistribution.hybrid > 0 ||
                     currentLocaleTrends.remotenessDistribution.onsite > 0))) && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl w-full flex items-center justify-center">
                    <DonutSunburstChart
                      outerData={currentLocaleTrends.locationDistribution || {}}
                      innerData={{
                        Remote: currentLocaleTrends.remotenessDistribution?.remote || 0,
                        Hybrid: currentLocaleTrends.remotenessDistribution?.hybrid || 0,
                        Onsite: currentLocaleTrends.remotenessDistribution?.onsite || 0,
                      }}
                      outerTitle={intl.formatMessage({ id: "jobDetailPage.locationDistribution", defaultMessage: "Location Distribution", description: "Chart title for location distribution showing where company posts jobs" })}
                      innerTitle={intl.formatMessage({ id: "jobDetailPage.remotenessDistribution.short", defaultMessage: "Remoteness", description: "Short label for remoteness distribution chart" })}
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                      className="w-full max-w-[300px] mx-auto"
                      maxInnerItems={3}
                      minOuterPercentage={3}
                      outerCaption={
                        <div className="text-xs text-center text-gray-600 dark:text-gray-400">
                          <FormattedMessage 
                            id="jobDetailPage.locationDistribution" 
                            defaultMessage="Location Distribution" 
                            description="Chart title for location distribution showing where company posts jobs" 
                          />
                        </div>
                      }
                      innerCaption={
                        <div className="text-xs text-center text-gray-500 dark:text-gray-500 mt-2">
                          <FormattedMessage 
                            id="jobDetailPage.byRemoteness" 
                            defaultMessage="By Remoteness" 
                            description="Chart caption label indicating data is grouped by work model (remote, hybrid, onsite)" 
                          />
                        </div>
                      }
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'languages' && currentLocaleTrends?.languageRequirements &&
                  Object.keys(currentLocaleTrends.languageRequirements).length > 0 &&
                  Object.values(currentLocaleTrends.languageRequirements).some(val => hasAnyValidNumber(val)) && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl w-full">
                    <StarburstChart
                      data={currentLocaleTrends.languageRequirements}
                      title={intl.formatMessage({ id: "jobDetailPage.languageRequirements", defaultMessage: "Language Requirements", description: "Chart title for language requirements distribution showing which languages companies typically require" })}
                      titleKey="languageRequirements"
                      brandColour={effectiveBrandColour}
                      companyName={companyId}
                      locale={jobLocale}
                    />
                  </div>
                )}

                {companyInfoActiveTab === 'map' && companyGeocoordinates && (
                  <div className="bg-gray-50 dark:bg-gray-900/50 p-4 rounded-xl w-full">
                    {showCompanyLocationMap ? (
                      <CompanyLocationMap
                        coordinates={companyGeocoordinates}
                        companyName={companyId}
                        address={addressDisplay}
                        companyId={companyId}
                      />
                    ) : (
                      <button
                        type="button"
                        onClick={() => setShowCompanyLocationMap(true)}
                        className="w-full px-4 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium cursor-pointer"
                      >
                        <FormattedMessage 
                          id="jobDetailPage.seeLocation" 
                          defaultMessage="See Location" 
                          description="Button label to view company location on a map" 
                        />
                      </button>
                    )}
                  </div>
                )}

                {/* Data range disclaimer */}
                {companyInfoActiveTab !== 'map' && (
                  <div className="mt-4 text-center">
                    <p className="text-xs text-gray-500 dark:text-gray-400">
                      <FormattedMessage 
                        id="jobDetailPage.chartsDataRange" 
                        defaultMessage="Data shown is based on historical job postings from our database."
                        description="Disclaimer text explaining that chart data is based on historical job posting data" 
                      />
                    </p>
                  </div>
                )}
              </div>

              {/* Bottom row tabs */}
              {bottomRowTabs.length > 0 && (
                <div className="mt-4">
                  <div className="flex gap-3 justify-center flex-wrap">
                    {bottomRowTabs.map(renderTabButton)}
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
});

CompanySection.displayName = 'CompanySection';
